import React from 'react';
import styled from 'styled-components';

import Button from '../../../Button';
import Dropdown, { StyledContent, StyledDropdownIcon } from '../../../Dropdown';
import OnClickOutside from '../../../OnClickOutside';
import ToggleButtonsGroup from '../../../ToggleButtonsGroup';
import Input from '../../../Input';
import Throbber from '../../../Throbber';
import PhoneMonitoring, { IPhoneMonitoring } from '../PhoneMonitoring';

import { Transfer } from '../../../../icons-react/dialer';
import { ValueOf } from '@utils/tsUtils';
import { VOX_STATES } from '../../../../consts/vox';

const StyledTransferIcon = styled(Transfer)`
  width: 23px;
  height: 19px;

  path {
    stroke: white;
  }
`;

const DEFAULT_PLACEHOLDER = 'Сотрудники';

const FILTER_BUTTONS = [
  {
    title: 'Все',
    value: 'all',
  },
  {
    title: 'Только онлайн',
    value: 'online',
  },
  {
    title: 'Только операторы',
    value: 'operators',
  },
];

export interface OperatorsDropdownProps
  extends Omit<IPhoneMonitoring, 'onToggle'> {
  state: ValueOf<typeof VOX_STATES>;
  callID?: string | null;
  className?: string;
  placeholder?: string;
  onToggle: ({ isOpen }: { isOpen: boolean }) => void;
}

interface OperatorsDropdownState {
  searchQuery: string;
  visibleOnly: string[];
  isOpen: boolean;
}

class OperatorsDropdown extends React.PureComponent<
  OperatorsDropdownProps,
  OperatorsDropdownState
> {
  static defaultProps = {
    placeholder: DEFAULT_PLACEHOLDER,
    searchQuery: '',
    visibleOnly: ['all'],
  };
  constructor(props) {
    super(props);

    this.state = {
      searchQuery: '',
      visibleOnly: ['all'],
      isOpen: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.inputRenderer = this.inputRenderer.bind(this);
    this.labelRenderer = this.labelRenderer.bind(this);
    this.operatorsRenderer = this.operatorsRenderer.bind(this);
  }

  handleChange({ target }) {
    const { value } = target;
    this.setState({
      searchQuery: value,
    });
  }

  handleFocus(ev) {
    const { isOpen } = this.state;

    if (isOpen) {
      ev.stopPropagation();
    }
  }

  handleOutsideClick() {
    const { isOpen } = this.state;
    if (isOpen) {
      this.handleToggle();
    }
  }

  handleFilterChange({ detail }) {
    const visibleOnly = detail.value;
    this.setState({ visibleOnly });
  }

  handleToggle() {
    const { onToggle } = this.props;

    this.setState(
      prevState => ({
        isOpen: !prevState.isOpen,
        searchQuery: '',
        visibleOnly: ['all'],
      }),
      () => {
        const { isOpen } = this.state;
        onToggle({ isOpen });
      }
    );
  }

  inputRenderer() {
    const { searchQuery } = this.state;
    const { placeholder } = this.props;

    return (
      <StyledInput
        value={searchQuery}
        placeholder={placeholder}
        onClick={this.handleFocus}
        onChange={this.handleChange}
      />
    );
  }

  labelRenderer() {
    const { state } = this.props;
    const isService = state === VOX_STATES.IN_SERVICE;

    if (isService) {
      return (
        <StyledTransferContainer>
          <StyledTransferIcon />
          {this.inputRenderer()}
        </StyledTransferContainer>
      );
    }

    return this.inputRenderer();
  }

  filterRenderer() {
    const { visibleOnly } = this.state;
    return (
      <StyledToggleButtonsGroup
        required
        key={visibleOnly[0]}
        onChange={this.handleFilterChange}
        values={visibleOnly}
      >
        {FILTER_BUTTONS.map(({ title, value }) => (
          <StyledButton key={value} value={value}>
            {title}
          </StyledButton>
        ))}
      </StyledToggleButtonsGroup>
    );
  }

  operatorsRenderer() {
    const { operators, onCall, onTransfer, onListen, onRequestHelp } =
      this.props;
    const { searchQuery, visibleOnly } = this.state;

    if (!operators) {
      return <StyledThrobber size={16} color="grey" />;
    }

    if (operators.length === 0) {
      return <StyledEmpty>Нет свободных отделов!</StyledEmpty>;
    }

    return (
      <StyledPopup>
        {this.filterRenderer()}
        <StyledPhoneMonitoring
          searchQuery={searchQuery}
          visibleOnly={visibleOnly}
          operators={operators}
          onCall={onCall}
          onTransfer={onTransfer}
          onListen={onListen}
          onRequestHelp={onRequestHelp}
        />
      </StyledPopup>
    );
  }

  render() {
    const { isOpen } = this.state;
    return (
      <StyledContainer {...this.props}>
        <OnClickOutside onClickOutside={this.handleOutsideClick}>
          <StyledOperatorsDropdown
            headRenderer={this.labelRenderer()}
            contentRenderer={this.operatorsRenderer()}
            onToggle={this.handleToggle}
            open={isOpen}
          />
        </OnClickOutside>
      </StyledContainer>
    );
  }
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledOperatorsDropdown = styled(Dropdown)`
  color: #fff;
  font-size: 13px;
  line-height: normal;
  border-radius: 4px;
  border: solid 1px #919499;
  background-color: #262729;

  ${StyledContent} {
    width: 530px;

    ul {
      width: 100%;
      flex-wrap: wrap;
      max-height: 480px;
      overflow-y: scroll;
      padding-right: 5px;

      ::-webkit-scrollbar {
        width: 4px;
      }

      ::-webkit-scrollbar-thumb {
        background-color: #c2c5cc;
        border-radius: 40px;
      }
    }
  }

  ${StyledDropdownIcon} {
    position: absolute;
    top: 50%;
    margin-top: -2px;
    right: 10px;
  }
`;

const StyledPopup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledInput = styled(Input)`
  flex: 1;
  width: 100%;
  height: 38px;
  border: none;
  color: white;
  font-size: 13px;
  line-height: 38px;
  font-weight: normal;
  background-color: #262729;
  padding: 10px 24px 10px 8px;
`;

const StyledTransferContainer = styled.div`
  color: #fff;
  display: flex;
  position: relative;
  align-items: center;

  ${StyledInput} {
    padding-left: 39px;
  }

  ${StyledTransferIcon} {
    left: 8px;
    margin-top: -9px;
    position: absolute;
    top: 50%;
    z-index: 1;
  }
`;

const StyledThrobber = styled(Throbber)`
  padding: 35px 0;
  position: relative;
`;

const StyledEmpty = styled.div`
  width: 100%;
  color: black;
  font-size: 14px;
  line-height: 70px;
  text-align: center;
`;

const StyledPhoneMonitoring = styled(PhoneMonitoring)`
  color: black;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  margin-right: 10px;
  background-color: white;

  ${p =>
    p.checked &&
    `
    color: white;
    background-color: #007aff;

    &:hover { color: white; }
  `}
`;

const StyledToggleButtonsGroup = styled(ToggleButtonsGroup)`
  height: auto;
  padding: 5px 8px 10px;
  ${StyledButton} {
    height: 36px;
    padding: 0 13px;
    line-height: 36px;
    border-radius: 36px;
    border: 1px solid #007aff;
  }
`;

export default OperatorsDropdown;
